import { ApolloError } from '@apollo/client';
import React, { createContext, useContext, useState } from 'react';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useAuth } from '../provider';
import { logout } from '../utils/auth';

interface Context {
    showError: (message?: string, fullError?: Error) => void;
}

interface ErrorProviderProps {
    children: React.ReactNode;
}

const ErrorContext = createContext<Context | null>(null);

const ErrorProvider = ({ children }: ErrorProviderProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { signOut } = useAuth();
    const [message, setMessage] = useState('');

    function showError(errorMessage?: string, fullError?: Error) {
        if (fullError instanceof ApolloError && errorMessage === undefined) {
            console.log('Error:', errorMessage);
            onOpen();

            let displayMessage = `Something unexpected occurred on our end!`;

            if (fullError instanceof ApolloError) {
                const { networkError } = fullError;
                const isUnauthorized = fullError.message.includes('401');

                if (networkError) {
                    if (isUnauthorized) {
                        displayMessage = 'You do not have permission to perform this action';
                        logout();
                        signOut();
                    }
                }
            }

            setMessage(displayMessage);
        }
    }

    const incidentId = sessionStorage.getItem('incidentId');

    return (
        <ErrorContext.Provider value={{ showError }}>
            {children}

            {isOpen && (
                <Modal isCentered isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Oops!</ModalHeader>
                        <ModalBody fontWeight={400}>
                            <Text>{message}</Text>
                            <Text>Please try again</Text>
                            <br />
                            <Text>Incident: {incidentId}</Text>
                        </ModalBody>

                        <ModalFooter justifyContent='center'>
                            <Button onClick={onClose}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </ErrorContext.Provider>
    );
};

export function useError() {
    const context = useContext(ErrorContext);

    if (context === null) {
        throw new Error('useError must be used within a ErrorProvider');
    }

    return context;
}

export default ErrorProvider;
